import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { store } from '../app/store';

// Tilt-Shift Shader Pass
const TiltShiftShader = {
  name: 'TiltShiftShader',
  uniforms: {
    tDiffuse: { value: null },
    focusPos: { value: 0.5 },
    focusRange: { value: 0.25 },
    blurSize: { value: 2.0 },
    resolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float focusPos;
    uniform float focusRange;
    uniform float blurSize;
    uniform vec2 resolution;
    varying vec2 vUv;

    void main() {
      float dist = abs(vUv.y - focusPos);
      float blur = smoothstep(focusRange * 0.5, focusRange, dist) * blurSize;

      vec4 color = vec4(0.0);
      vec2 texel = vec2(1.0 / resolution.x, 1.0 / resolution.y);

      // 9-tap separable blur
      color += texture2D(tDiffuse, vUv + vec2(-2.0, -2.0) * texel * blur) * 0.06;
      color += texture2D(tDiffuse, vUv + vec2(0.0, -2.0) * texel * blur) * 0.09;
      color += texture2D(tDiffuse, vUv + vec2(2.0, -2.0) * texel * blur) * 0.06;
      color += texture2D(tDiffuse, vUv + vec2(-2.0, 0.0) * texel * blur) * 0.12;
      color += texture2D(tDiffuse, vUv) * 0.34;
      color += texture2D(tDiffuse, vUv + vec2(2.0, 0.0) * texel * blur) * 0.12;
      color += texture2D(tDiffuse, vUv + vec2(-2.0, 2.0) * texel * blur) * 0.06;
      color += texture2D(tDiffuse, vUv + vec2(0.0, 2.0) * texel * blur) * 0.09;
      color += texture2D(tDiffuse, vUv + vec2(2.0, 2.0) * texel * blur) * 0.06;

      gl_FragColor = color;
    }
  `,
};

// Retro Pixel Art Shader Pass
const PixelArtShader = {
  name: 'PixelArtShader',
  uniforms: {
    tDiffuse: { value: null },
    pixelSize: { value: 4.0 },
    resolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float pixelSize;
    uniform vec2 resolution;
    varying vec2 vUv;

    void main() {
      vec2 coord = floor(vUv * (resolution / pixelSize)) / (resolution / pixelSize);
      vec4 col = texture2D(tDiffuse, coord);
      // Subtle palette quantization
      col.rgb = floor(col.rgb * 16.0) / 16.0;
      gl_FragColor = col;
    }
  `,
};

// Chinese Ink Lines Edge Shader (Sobel)
const InkLinesShader = {
  name: 'InkLinesShader',
  uniforms: {
    tDiffuse: { value: null },
    resolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
    lineThickness: { value: 1.2 },
  },
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform vec2 resolution;
    uniform float lineThickness;
    varying vec2 vUv;

    void main() {
      vec2 d = (1.0 / resolution) * lineThickness;
      vec4 c = texture2D(tDiffuse, vUv);

      vec4 n0 = texture2D(tDiffuse, vUv + vec2(-d.x, -d.y));
      vec4 n1 = texture2D(tDiffuse, vUv + vec2(0.0, -d.y));
      vec4 n2 = texture2D(tDiffuse, vUv + vec2(d.x, -d.y));
      vec4 n3 = texture2D(tDiffuse, vUv + vec2(-d.x, 0.0));
      vec4 n5 = texture2D(tDiffuse, vUv + vec2(d.x, 0.0));
      vec4 n6 = texture2D(tDiffuse, vUv + vec2(-d.x, d.y));
      vec4 n7 = texture2D(tDiffuse, vUv + vec2(0.0, d.y));
      vec4 n8 = texture2D(tDiffuse, vUv + vec2(d.x, d.y));

      vec4 sobelH = (n2 + 2.0 * n5 + n8) - (n0 + 2.0 * n3 + n6);
      vec4 sobelV = (n6 + 2.0 * n7 + n8) - (n0 + 2.0 * n1 + n2);
      float edge = length(sobelH.rgb) + length(sobelV.rgb);

      // Darken ink edges
      float ink = smoothstep(0.35, 0.8, edge);
      vec3 finalCol = mix(c.rgb, c.rgb * 0.15, ink * 0.7);

      gl_FragColor = vec4(finalCol, c.a);
    }
  `,
};

export class PostProcessingPipeline {
  private composer: EffectComposer;
  private renderer: THREE.WebGLRenderer;
  private renderPass: RenderPass;
  private bloomPass: UnrealBloomPass;
  private tiltShiftPass: ShaderPass;
  private pixelArtPass: ShaderPass;
  private inkLinesPass: ShaderPass;

  constructor(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera) {
    this.renderer = renderer;

    const width = window.innerWidth;
    const height = window.innerHeight;

    this.composer = new EffectComposer(renderer);
    this.renderPass = new RenderPass(scene, camera);
    this.composer.addPass(this.renderPass);

    // Bloom
    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(width, height),
      0.35, // strength
      0.4,  // radius
      0.85  // threshold
    );
    this.composer.addPass(this.bloomPass);

    // Tilt Shift
    this.tiltShiftPass = new ShaderPass(TiltShiftShader);
    this.composer.addPass(this.tiltShiftPass);

    // Pixel Art
    this.pixelArtPass = new ShaderPass(PixelArtShader);
    this.composer.addPass(this.pixelArtPass);

    // Ink Lines
    this.inkLinesPass = new ShaderPass(InkLinesShader);
    this.composer.addPass(this.inkLinesPass);

    this.updatePassStates();
  }

  public setSize(w: number, h: number): void {
    this.composer.setSize(w, h);
    TiltShiftShader.uniforms.resolution.value.set(w, h);
    PixelArtShader.uniforms.resolution.value.set(w, h);
    InkLinesShader.uniforms.resolution.value.set(w, h);
  }

  public updatePassStates(): void {
    const s = store.getState();
    const isLowOrMobile = s.quality === 'low' || s.optimizePerformance;

    // Zero cost when disabled
    this.tiltShiftPass.enabled = s.tiltShift && !isLowOrMobile;
    this.pixelArtPass.enabled = s.pixelArt;
    this.inkLinesPass.enabled = s.inkLines && !isLowOrMobile;
    this.bloomPass.enabled = s.bloom && !isLowOrMobile && (s.quality === 'medium' || s.quality === 'high');
  }

  public isAnyPassActive(): boolean {
    return (
      this.tiltShiftPass.enabled ||
      this.pixelArtPass.enabled ||
      this.inkLinesPass.enabled ||
      this.bloomPass.enabled
    );
  }

  public render(dt: number): void {
    this.updatePassStates();

    // If zero post-processing passes are enabled, render directly for 100% native performance
    if (!this.isAnyPassActive()) {
      this.renderer.render(this.renderPass.scene, this.renderPass.camera);
    } else {
      this.composer.render(dt);
    }
  }

  public setCamera(cam: THREE.Camera): void {
    this.renderPass.camera = cam;
  }

  public dispose(): void {
    this.composer.dispose();
  }
}
